/**
 * Shared Gail API error-code handling.
 *
 * API error bodies look like:
 * { "errors": [{ "code": "ERR_...", "detail": "...", "status": "400", "title": "..." }] }
 *
 * Call sites should key off code via translateGailApiErrors rather than
 * hard-coding strings, so scripts / use cases / campaigns can share one registry.
 *
 * Translations live in the errors i18n namespace under public/locales,
 * keyed by the API code (and optional _NAMED variants for title interpolation).
 */

import { getBrowserPostHog } from "@/lib/posthog-browser";

export type GailApiError = {
  code: string;
  detail: string;
  status: string;
  title: string;
};

export type GailApiErrorTranslateFn = (
  key: string,
  options?: Record<string, unknown>
) => string;

type GailApiErrorCodeConfig = {
  /** i18n key under the errors namespace (typically the API code itself). */
  key: string;
  /**
   * Optional key that interpolates a resource name via title.
   * Used when TranslateGailApiErrorsContext.title is provided.
   */
  namedKey?: string;
};

/**
 * Known API errors[].code values mapped to errors-namespace translation keys.
 * Add new codes here and in public/locales errors.json so every feature can reuse them.
 */
export const GAIL_API_ERROR_CODES: Record<string, GailApiErrorCodeConfig> = {
  ERR_DESCRIPTION_REQUIRED: {
    key: "ERR_DESCRIPTION_REQUIRED",
    namedKey: "ERR_DESCRIPTION_REQUIRED_NAMED",
  },
  ERR_AGENTNAME_REQUIRED: { key: "ERR_AGENTNAME_REQUIRED" },
  ERR_GREETING_REQUIRED: { key: "ERR_GREETING_REQUIRED" },
  ERR_GOODBYE_REQUIRED: { key: "ERR_GOODBYE_REQUIRED" },
  ERR_USECASES_NONEMPTY: { key: "ERR_USECASES_NONEMPTY" },
  ERR_VOICE_REQUIRED: { key: "ERR_VOICE_REQUIRED" },
  ERR_NAME_REQUIRED: { key: "ERR_NAME_REQUIRED" },
};

export type TranslateGailApiErrorsContext = {
  /** Resource name (e.g. use case title) for codes that support named messages. */
  title?: string;
};

export function normalizeGailApiErrors(payload: unknown): GailApiError[] {
  if (Array.isArray(payload)) return payload as GailApiError[];

  if (
    payload &&
    typeof payload === "object" &&
    "errors" in payload &&
    Array.isArray((payload as Record<string, unknown>).errors)
  ) {
    return (payload as { errors: GailApiError[] }).errors;
  }

  return [];
}

export function isGailApiErrorPayload(payload: unknown): boolean {
  return normalizeGailApiErrors(payload).length > 0;
}

/**
 * Returns a trimmed use-case id when the response is a successful object body,
 * otherwise null. Safe with alwaysReturn primitives / error envelopes.
 */
export function getSuccessfulUseCaseId(response: unknown): string | null {
  if (!response || typeof response !== "object" || isGailApiErrorPayload(response)) {
    return null;
  }
  const id = (response as { id?: unknown }).id;
  if (typeof id !== "string") return null;
  const trimmed = id.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Report an expected use-case save failure to PostHog (and console) so inbound
 * and outbound callers share the same diagnostic path while keeping UI alerts
 * non-throwing.
 */
export function reportUseCaseSaveFailed(context?: {
  failedUseCaseId?: string;
  failedUseCaseTitle?: string;
}): void {
  console.error("Use case save failed", context ?? {});
  getBrowserPostHog()?.captureException(new Error("Use case save failed"));
}

/**
 * Accepts a raw Gail API error payload and returns translated, user-facing
 * messages. Falls back to detail, then title, then code when no translation is
 * registered for a given code.
 *
 * Pass a t bound to the errors namespace (or any t that resolves the ERR_* keys).
 */
export function translateGailApiErrors(
  payload: unknown,
  t: GailApiErrorTranslateFn,
  context?: TranslateGailApiErrorsContext
): string[] {
  const errors = normalizeGailApiErrors(payload);
  if (errors.length === 0) return [];

  const title = context?.title?.trim() || undefined;

  return errors.map((err) => {
    const config = GAIL_API_ERROR_CODES[err.code];
    if (config) {
      const key = title && config.namedKey ? config.namedKey : config.key;
      const translated = title && config.namedKey
        ? t(key, { title })
        : t(key);
      if (translated !== key) return translated;
    }
    return err.detail || err.title || err.code;
  });
}

export type FormatUseCaseSaveFailureArgs = {
  apiErrorPayload?: unknown;
  failedUseCaseTitle?: string;
  /** t that resolves ERR_* keys (typically from the errors namespace). */
  t: GailApiErrorTranslateFn;
  /** Already-translated generic fallback, e.g. Could not save actions. */
  fallback: string;
};

/**
 * Prefer known API error messages (with optional use-case title); otherwise
 * the generic fallback, optionally suffixed with the title.
 */
export function formatUseCaseSaveFailureMessage({
  apiErrorPayload,
  failedUseCaseTitle,
  t,
  fallback,
}: FormatUseCaseSaveFailureArgs): string {
  const translated = translateGailApiErrors(apiErrorPayload, t, {
    title: failedUseCaseTitle,
  });
  if (translated.length > 0) {
    return translated.join(", ");
  }

  const detail = failedUseCaseTitle?.trim()
    ? `: ${failedUseCaseTitle.trim()}`
    : "";
  return `${fallback}${detail}`;
}
